import { useState } from "react";
import { Mail, ShieldAlert, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/firebase/useAuth";

interface ParentalApprovalRequiredProps {
  parentEmail?: string | null;
}

export default function ParentalApprovalRequired({
  parentEmail,
}: ParentalApprovalRequiredProps) {
  const { user } = useAuth();
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  
  // Use system dark mode preference since this renders before ThemeProvider
  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;

  const handleResendEmail = async () => {
    if (!user || !parentEmail) return;

    setResending(true);
    setResendMessage(null);

    try {
      const response = await fetch("/api/auth/send-parent-approval", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          childUid: user.uid,
          childEmail: user.email || null,
          childDisplayName: user.displayName || null,
          parentEmail,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to resend approval email");
      }

      setResendMessage("Email sent! Check your parent's inbox.");
    } catch (error) {
      setResendMessage("Failed to resend. Please try again later.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center",
        isDark ? "bg-slate-950/95" : "bg-white/95"
      )}
    >
      <div
        className={cn(
          "mx-4 max-w-md rounded-xl border p-8 shadow-2xl",
          isDark
            ? "border-slate-700 bg-slate-900"
            : "border-slate-200 bg-white"
        )}
      >
        <div className="mb-6 flex justify-center">
          <div
            className={cn(
              "rounded-full p-4",
              isDark ? "bg-amber-900/30" : "bg-amber-100"
            )}
          >
            <ShieldAlert
              className={cn(
                "h-12 w-12",
                isDark ? "text-amber-400" : "text-amber-600"
              )}
            />
          </div>
        </div>

        <h2
          className={cn(
            "mb-4 text-center text-2xl font-bold",
            isDark ? "text-white" : "text-slate-900"
          )}
        >
          Waiting for Parent Approval
        </h2>

        <div className="space-y-4">
          <p
            className={cn(
              "text-center",
              isDark ? "text-slate-300" : "text-slate-700"
            )}
          >
            Your parent has not approved your account yet. Please talk with your
            parent to confirm approval first.
          </p>

          {parentEmail && (
            <div
              className={cn(
                "flex items-start gap-3 rounded-lg border p-4",
                isDark
                  ? "border-slate-700 bg-slate-800/50"
                  : "border-slate-200 bg-slate-50"
              )}
            >
              <Mail
                className={cn(
                  "mt-0.5 h-5 w-5 flex-shrink-0",
                  isDark ? "text-slate-400" : "text-slate-500"
                )}
              />
              <div className="flex-1">
                <p
                  className={cn(
                    "text-sm font-medium",
                    isDark ? "text-slate-200" : "text-slate-800"
                  )}
                >
                  Approval email sent to:
                </p>
                <p
                  className={cn(
                    "text-sm",
                    isDark ? "text-slate-400" : "text-slate-600"
                  )}
                >
                  {parentEmail}
                </p>
              </div>
            </div>
          )}

          <div
            className={cn(
              "rounded-lg border p-4",
              isDark
                ? "border-blue-800/50 bg-blue-900/20"
                : "border-blue-200 bg-blue-50"
            )}
          >
            <p
              className={cn(
                "text-sm",
                isDark ? "text-blue-200" : "text-blue-800"
              )}
            >
              <strong>What happens next?</strong>
              <br />
              Once your parent clicks the approval link in their email, you'll
              be able to access all of ZillHub's features.
            </p>
          </div>

          <p
            className={cn(
              "text-center text-sm",
              isDark ? "text-slate-400" : "text-slate-500"
            )}
          >
            If your parent didn't receive the email, ask them to check their
            spam folder.
          </p>

          {/* Resend Email Button */}
          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              onClick={handleResendEmail}
              disabled={resending}
              className={cn(
                "flex items-center gap-2 rounded-lg px-6 py-3 font-medium transition-all",
                "disabled:cursor-not-allowed disabled:opacity-50",
                isDark
                  ? "bg-slate-700 text-slate-200 hover:bg-slate-600"
                  : "bg-slate-200 text-slate-800 hover:bg-slate-300"
              )}
            >
              <RefreshCw
                className={cn("h-4 w-4", resending && "animate-spin")}
              />
              {resending ? "Sending..." : "Resend Approval Email"}
            </button>

            {resendMessage && (
              <p
                className={cn(
                  "text-sm font-medium",
                  resendMessage.includes("sent")
                    ? isDark
                      ? "text-green-400"
                      : "text-green-600"
                    : isDark
                    ? "text-red-400"
                    : "text-red-600"
                )}
              >
                {resendMessage}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
